<script setup>
import { useVModel } from "@vueuse/core";
import { debounce } from "lodash";
import { computed, defineEmits, defineProps, ref } from "vue";
import { addedOptions } from "@/components/program-manager/sessions/components/Academics/constants";
import { convertClassFilterCriteria } from "@/components/program-manager/sessions/utils";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import { useGetClassOptions } from "@/providers/classes/useGetClassOptions";

// The track configurator renders one of these per library class across a recursive
// rule tree, all sharing the same filters. Deriving the query key from the params
// keeps that whole tree on a single request, and refetches when the filters change.
const CLASS_OPTIONS_STALE_TIME = 5 * 60 * 1000;

const props = defineProps({
  modelValue: {
    type: Object,
  },
  filters: {
    type: Object,
    required: true,
  },
  onRemove: {
    type: Function,
    required: true,
  },
});
const emit = defineEmits(["update:modelValue"]);
const libraryClass = useVModel(props, "modelValue", emit);

const search = ref("");

const classesParams = computed(() => ({
  ...convertClassFilterCriteria(props.filters),
  limit: 10,
  skip: 0,
  ...(search.value ? { q: search.value, get_archived: false } : {}),
}));

const { data: classesOptions, isFetching } = useGetClassOptions({
  params: classesParams,
  useQueryOptions: { staleTime: CLASS_OPTIONS_STALE_TIME },
});

const onSearch = debounce((value) => {
  search.value = value;
}, 250);
</script>

<template>
  <div class="flex align-baseline items-center">
    <SearchableSelect
      v-model="libraryClass"
      :options="[...(classesOptions || []), ...addedOptions]"
      class="w-full"
      placeholder="Search and select a Class"
      label="title"
      scroll-input="hidden"
      :loading="isFetching"
      @search="onSearch"
    />
    <TrashCan class="w-[18px] mx-6 cursor-pointer" @click="props.onRemove" />
  </div>
</template>
